import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { UserState } from './core/state/user/user.reducer';
import { Store } from '@ngrx/store';
import { AuthTokenService } from './core/services/auth-token.service';
import { loadUser } from './core/state/user/user.actions';
import { CommonModule, DOCUMENT } from '@angular/common';
import { filter, map, mergeMap, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CookieBannerComponent } from './shared/components/molecules/cookie-banner/cookie-banner.component';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsConsentService } from './core/services/analytics-consent.serivice';
import { GoogleAnalyticsService } from './core/services/google-analytics.service';

const PUBLIC_SITE_URL = 'https://ecofeast.app';
const SOCIAL_IMAGE_URL = `${PUBLIC_SITE_URL}/images/ecofeast_mockup.png`;

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, CommonModule, MatSnackBarModule, CookieBannerComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  constructor(
    private userStore: Store<UserState>,
    private authTokenService: AuthTokenService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private meta: Meta,
    private translate: TranslateService,
    private consent: AnalyticsConsentService,
    private ga: GoogleAnalyticsService,
    @Inject(DOCUMENT) private document: Document
  ) {
    // If user already accepted earlier, init GA immediately
    if (this.consent.hasAnalyticsConsent()) {
      this.ga.init();
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),

        // GA SPA page tracking (only if consent accepted)
        tap((event) => {
          if (this.consent.hasAnalyticsConsent()) {
            this.ga.trackPage(event.urlAfterRedirects);
          }
        }),

        // Find deepest child route for data
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data),

        // Translate title + description keys (if provided)
        switchMap((data) => {
          const titleKey = data['title'] as string | undefined;
          const descKey = data['description'] as string | undefined;

          // If you don’t set description on some routes, keep a sane default
          const safeTitleKey = titleKey ?? 'titles.default';
          const safeDescKey = descKey ?? 'meta.default.description';
          const socialImageAltKey = 'meta.social.imageAlt';

          return this.translate.stream([safeTitleKey, safeDescKey, socialImageAltKey]).pipe(
            map((t) => ({
              title: t[safeTitleKey] ?? '',
              description: t[safeDescKey] ?? '',
              socialImageAlt: t[socialImageAltKey] ?? 'EcoFeast'
            }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ title, description, socialImageAlt }) => {
        const canonicalUrl = this.getCanonicalUrl();
        const currentLanguage = this.translate.currentLang || this.translate.getDefaultLang();

        // document title
        if (title) this.titleService.setTitle(title);

        // meta description
        if (description) {
          this.meta.updateTag({ name: 'description', content: description });
        }

        if (title) this.meta.updateTag({ property: 'og:title', content: title });
        if (description) this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:site_name', content: 'EcoFeast' });
        this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
        this.meta.updateTag({ property: 'og:image', content: SOCIAL_IMAGE_URL });
        this.meta.updateTag({ property: 'og:image:alt', content: socialImageAlt });
        this.meta.updateTag({ property: 'og:locale', content: currentLanguage === 'hu' ? 'hu_HU' : 'en_GB' });

        this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
        if (title) this.meta.updateTag({ name: 'twitter:title', content: title });
        if (description) this.meta.updateTag({ name: 'twitter:description', content: description });
        this.meta.updateTag({ name: 'twitter:image', content: SOCIAL_IMAGE_URL });
        this.meta.updateTag({ name: 'twitter:image:alt', content: socialImageAlt });

        this.updateCanonicalLink(canonicalUrl);
      });
  }

  ngOnInit(): void {
    const token = this.authTokenService.getToken();
    if (token) {
      // Extract userId from the token and load user data
      const decodedToken: any = this.authTokenService.decodeToken();
      this.userStore.dispatch(loadUser({ userId: decodedToken.userId }));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCanonicalUrl(): string {
    const routePath = this.router.url.split(/[?#]/)[0] || '/';
    const normalizedPath = routePath === '/' ? '/' : routePath.replace(/\/+$/, '');
    return `${PUBLIC_SITE_URL}${normalizedPath}`;
  }

  private updateCanonicalLink(url: string): void {
    let canonical = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', url);
  }
}
